import { ChangeDetectionStrategy, Component, DestroyRef, Input, NgZone } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AlertController, IonButton, IonButtons, IonHeader, IonIcon, IonTitle, IonToolbar, ModalController } from '@ionic/angular/standalone';
import { Store } from '@ngrx/store';
import { formatISO } from 'date-fns';
import { map, take } from 'rxjs';
import { ButtonComponent } from 'src/app/components/button/button.component';
import { SpinnerComponent } from 'src/app/components/spinner/spinner.component';
import { DateTimeControl } from 'src/app/form-controls/datetime/datetime.control';
import { SelectControl, SelectItem } from 'src/app/form-controls/select/select.control';
import { TextInputControl } from 'src/app/form-controls/text-input/text-input.control';
import { TextAreaControl } from 'src/app/form-controls/textarea/textarea.control';
import { UserPickerControl } from 'src/app/form-controls/user-picker/user-picker.control';
import { SearchedUser } from 'src/app/services/users.service';
import { removeCommunicationRecordRequested, saveCommunicationRecordRequested } from 'src/app/store/patient-comms/patient-comms.actions';
import { AppState } from 'src/app/store/reducers';
import { profileInfo } from 'src/app/store/user-graph/user-graph.selectors';
import { CommunicationMethod, CommunicationRecord } from 'src/app/types/aggregate-graph.types';

interface CommunicationsRecordFormGroup {
	recordId: FormControl<number>;
	clientContactName: FormControl<string | null>;
	hospitalContactUserId: FormControl<number | null>;
	hospitalContactName: FormControl<string | null>;
	communicationDate: FormControl<string>;
	communicationMethod: FormControl<CommunicationMethod | null>;
	communicationText: FormControl<string | null>;
}

@Component({
	templateUrl: './communications-record.modal.html',
	styleUrls: ['./communications-record.modal.scss'],
	imports: [
		IonHeader, IonToolbar, IonTitle, IonButtons, IonButton, IonIcon, ReactiveFormsModule, TextInputControl,
		UserPickerControl, DateTimeControl, SelectControl, TextAreaControl, ButtonComponent, SpinnerComponent
	],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class CommunicationsRecordModal {

	form: FormGroup<CommunicationsRecordFormGroup> | null = null;
	hospitalContactUserControl: FormControl<SearchedUser | null> = new FormControl<SearchedUser | null>(null, [Validators.required]);

	communicationMethodItems: SelectItem[] = [
		{ value: CommunicationMethod.Email, text: 'Email', svgIconName: 'at-sharp', svgIconClass: 'svg-icon-grey' },
		{ value: CommunicationMethod.InPerson, text: 'In Person', svgIconName: 'people-sharp', svgIconClass: 'svg-icon-grey' },
		{ value: CommunicationMethod.Telephone, text: 'Telephone', svgIconName: 'call-sharp', svgIconClass: 'svg-icon-grey' },
		{ value: CommunicationMethod.Other, text: 'Other', svgIconName: 'help-sharp', svgIconClass: 'svg-icon-grey' }
	];

	@Input() set record(record: CommunicationRecord | null) {
		// handle both the new and existing scenarios
		if (record) {
			this.form = this.formBuilder.group<CommunicationsRecordFormGroup>({
				recordId: this.formBuilder.nonNullable.control<number>(record!.id, [Validators.required]),
				clientContactName: this.formBuilder.control<string>(record!.clientContactName, [Validators.required]),
				hospitalContactUserId: this.formBuilder.control<number>(record!.hospitalContactUserId, [Validators.required]),
				hospitalContactName: this.formBuilder.control<string>(record!.hospitalContactName, [Validators.required]),
				communicationDate: this.formBuilder.nonNullable.control<string>(record!.communicationDate, [Validators.required]),
				communicationMethod: this.formBuilder.control<CommunicationMethod>(record!.communicationMethod, [Validators.required]),
				communicationText: this.formBuilder.control<string>(record!.communicationText, [Validators.required])
			});
		}
		else {
			this.store.select(profileInfo).pipe(
				take(1),
				map((profileInfo) => this.formBuilder.group<CommunicationsRecordFormGroup>({
					recordId: this.formBuilder.nonNullable.control<number>(0, [Validators.required]),
					clientContactName: this.formBuilder.control<string | null>(null, [Validators.required]),
					hospitalContactUserId: this.formBuilder.control<number>(profileInfo.userId, [Validators.required]),
					hospitalContactName: this.formBuilder.control<string>(profileInfo.fullName, [Validators.required]),
					communicationDate: this.formBuilder.nonNullable.control<string>(formatISO(new Date()), [Validators.required]),
					communicationMethod: this.formBuilder.control<CommunicationMethod | null>(null, [Validators.required]),
					communicationText: this.formBuilder.control<string | null>(null, [Validators.required])
				}))
			).subscribe(form => this.form = form);
		}

		// storing user info in separate control to the form (syncs user-picker control with Hospital Contact)
		this.hospitalContactUserControl.setValue({
			id: this.form!.value.hospitalContactUserId!,
			fullname: this.form!.value.hospitalContactName!,
			// properties not used but needed for the control
			aggregateId: '',
			initials: '',
			isActive: true,
			roles: []
		});
		this.hospitalContactUserControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(user => {
			if (user) {
				this.form!.patchValue({ hospitalContactUserId: user.id, hospitalContactName: user.fullname });
			}
			else {
				this.form!.patchValue({ hospitalContactUserId: null, hospitalContactName: null });
			}
		});
	}

	constructor(
		private formBuilder: FormBuilder, private store: Store<AppState>, private destroyRef: DestroyRef,
		private alertController: AlertController, private ngZone: NgZone, private modalController: ModalController) {
	}

	async saveEntry() {
		if (this.form!.valid) {
			const record: CommunicationRecord = {
				id: this.form!.value.recordId!,
				communicationDate: this.form!.value.communicationDate!,
				communicationMethod: this.form!.value.communicationMethod!,
				communicationText: this.form!.value.communicationText!,
				clientContactName: this.form!.value.clientContactName!,
				hospitalContactUserId: this.form!.value.hospitalContactUserId!,
				hospitalContactName: this.form!.value.hospitalContactName!
			};
			this.store.dispatch(saveCommunicationRecordRequested({ record }));
			this.close();
		}
		else {
			const alert = await this.alertController.create({
				header: 'Invalid entry',
				message: 'All fields are required',
				buttons: ['Ok'],
				backdropDismiss: false
			});
			await alert.present();
		}
	}

	async removeEntry() {
		if (this.form!.value.recordId) {
			const alert = await this.alertController.create({
				header: 'Remove entry',
				message: 'This will permanently remove the entry. Remove anyway?',
				buttons: [
					'No',
					{
						text: 'Yes',
						handler: () => {
							this.ngZone.run(() => {
								this.store.dispatch(removeCommunicationRecordRequested({ recordId: this.form!.value.recordId! }));
								this.close();
							});
						}
					}
				],
				backdropDismiss: false
			});
			alert.present();
		}
	}

	close() {
		this.modalController.dismiss();
	}
}
